import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BadgeCheck, Link2Off } from 'lucide-react';
import { checkVerifyToken, verifyEmail, resendVerification } from '../services/api';

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('validating'); // validating | success | expired | invalid
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    verifyEmail(token)
      .then(data => {
        setStatus('success');
        if (data.token) {
          // Auto-login
          const SecureLS = require('secure-ls');
          const ls = new SecureLS({ encodingType: 'aes' });
          ls.set('authToken', data.token);
          localStorage.setItem('isLoggedIn', 'true');
        }
      })
      .catch(err => {
        const code = err.response?.data?.code;
        setStatus(code === 'token_expired' ? 'expired' : 'invalid');
        setEmail(err.response?.data?.email || '');
      });
  }, [token]);

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    try {
      await resendVerification(email);
      setResent(true);
    } catch (e) {}
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 max-w-md w-full text-center">

        {status === 'validating' && (
          <>
            <div className="w-12 h-12 border-4 border-[#556B2F] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-700" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              Verifying your email...
            </h2>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#EDF3E0] flex items-center justify-center mx-auto mb-4">
              <BadgeCheck size={32} color="#556B2F" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              You're all set!
            </h2>
            <p className="text-base text-gray-600 mb-6">
              Your email has been confirmed. Welcome to 1G2N.
            </p>
            <button onClick={() => navigate('/my-life')}
              className="w-full bg-[#556B2F] text-white font-bold py-3 rounded-lg hover:bg-[#4A5D23]"
            >
              Continue to My Life
            </button>
          </>
        )}

        {(status === 'expired' || status === 'invalid') && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#FFF6E6] flex items-center justify-center mx-auto mb-4">
              <Link2Off size={32} color="#B5760A" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              {status === 'expired' ? 'This link has expired' : 'Invalid link'}
            </h2>
            <p className="text-base text-gray-600 mb-6">
              {status === 'expired'
                ? 'Verification links expire after 24 hours and can only be used once.'
                : 'This verification link is not valid. It may have been used already.'}
            </p>
            {email && !resent && (
              <button onClick={handleResend} disabled={resending}
                className="w-full bg-[#556B2F] text-white font-bold py-3 rounded-lg hover:bg-[#4A5D23] mb-3"
              >
                {resending ? 'Sending...' : 'Send a new link'}
              </button>
            )}
            {resent && <p className="text-[#556B2F] font-semibold mb-3">New link sent — check your inbox.</p>}
            <button onClick={() => navigate('/login')}
              className="w-full border-2 border-gray-300 text-gray-600 font-bold py-3 rounded-lg hover:bg-gray-50"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
